import React, {useState, useEffect} from 'react'
import {ValueInputWithRange} from '../../../sharedComponents/components'
import {useTranslation} from 'react-i18next'
import {colors} from '../../../sharedComponents/colors'

import AromiDishSelector from './AromiDishSelector'
import InputWithTags from './InputWithTags'
import PerformTaskForm from './PerformTaskForm'
import {handleFloatInput} from '../../../config/utils'
import {EventStatusCode, PerformableTask} from '../../../state/performTask/state'
import PerformTaskTitle from './PerformTaskTitle'
import {FormContainer} from './PerformCoolerCoolingTask'
import {useAppState} from '../../../state'
import ReactDatePicker from 'react-datepicker'
import styled from 'styled-components'
import {startOfMonth, subMonths, endOfDay, isSameDay} from 'date-fns'

interface PerformHygieneTaskProps {
  task: PerformableTask
  setValue: any
  onAssetSaved: () => void
  onAssetRemoved: (assetId: string) => void
  payload: any
  handleNumberBlur: (event: any) => void
  handleRemove: () => void
  hasCurrentEvent?: boolean
  currentEventId?: string
}

const CustomDateInputWrapper = styled.div`
  position: relative;
  width: 100%;

  /* react-datepicker renders a clear button with class 'react-datepicker__close-icon' inside the input wrapper
     We'll target the SVG path inside it to change the color depending on wrapper class. */
  &.default-date {
    /* when our custom input has this class, the close icon is rendered as a sibling by react-datepicker */
    & + .react-datepicker__close-icon,
    & + .react-datepicker__close-icon::after,
    & + .react-datepicker__close-icon svg {
      cursor: pointer;
      background-color: #e5e5ea !important;
      color: #000;
      border-radius: 50%;
      height: 25px !important;
      width: 25px !important;
      padding: 0px;
      font-size: 16px;
      line-height: 1;
      text-align: center;
      display: table-cell;
      vertical-align: middle;
      content: '×';
      margin-top: 6px;
      margin-right: 4px;
    }
    & + .react-datepicker__close-icon svg path {
      stroke: #e5e5ea !important;
      fill: #e5e5ea !important;
    }
  }

  &.changed-date {
    & + .react-datepicker__close-icon,
    & + .react-datepicker__close-icon::after,
    & + .react-datepicker__close-icon svg {
      cursor: pointer;
      background-color: #edc7c9 !important;
      color: #000;
      border-radius: 50%;
      height: 25px !important;
      width: 25px !important;
      padding: 0px;
      font-size: 16px;
      line-height: 1;
      text-align: center;
      display: table-cell;
      vertical-align: middle;
      content: '×';
      margin-top: 6px;
      margin-right: 4px;
    }
    & + .react-datepicker__close-icon svg path {
      stroke: #40a8f5 !important;
      fill: #40a8f5 !important;
    }
  }
`

const CustomInputField = styled.input`
  width: 100%;
  padding: 0.5rem 2.5rem 0.5rem 0.5rem;
  border: 1px solid ${colors.system.grey_5};
  border-radius: 4px;
  height: 38px;
  box-sizing: border-box;
`

const CustomDateInput = React.forwardRef(({value, onClick, className}: any, ref: any) => (
  <CustomDateInputWrapper className={className}>
    <CustomInputField readOnly ref={ref} value={value} onClick={onClick} />
  </CustomDateInputWrapper>
))

const PerformHygieneTask = ({
  task,
  setValue,
  onAssetSaved,
  onAssetRemoved,
  payload,
  handleNumberBlur,
  handleRemove,
  hasCurrentEvent,
  currentEventId
}: PerformHygieneTaskProps) => {
  const {t} = useTranslation('tasks')
  const {state} = useAppState()
  const [isRequired, setIsRequired] = useState(true)
  const handleRemoveTask = () => {
    setIsRequired(false)
    if (handleRemove) {
      handleRemove()
    }
  }
  // ensure payload.samplecollectionDate defaults to today when not provided
  const today = endOfDay(new Date())
  useEffect(() => {
    if (!payload.samplecollectionDate) {
      try {
        setValue('samplecollectionDate', new Date(today).toISOString())
      } catch (e) {
        // ignore if setValue cannot be called here
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isSampleCollectionDateDefault = (() => {
    if (!payload.samplecollectionDate) return true
    try {
      return isSameDay(new Date(payload.samplecollectionDate), today)
    } catch (e) {
      return false
    }
  })()
  return (
    <FormContainer>
      <PerformTaskTitle task={task} />
      <ValueInputWithRange
        name="value-input"
        value={payload.value}
        label={t('tasks:labels.measuredValue', 'Measured value')}
        onChange={e => setValue('value', handleFloatInput(e.target.value))}
        onBlur={e => handleNumberBlur(e.target.value)}
        unit={task.unit || 'RLU'}
        maxValue={task.maxValue || undefined}
        message={{
          success: t('tasks:inputWithRange.withinTarget', 'The value is within target limits'),
          error: t('tasks:inputWithRange.not.withInTarget', 'The value is not within target')
        }}
        required={isRequired}
      />
      {/* Sample collection date (single date) - shown under Measured value */}
      <div style={{marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.25rem'}}>
        <label style={{fontSize: '0.89rem'}}>{t('tasks:labels.sampleCollectionDate', 'Sample collection date')}</label>
        <ReactDatePicker
          selected={payload.samplecollectionDate ? new Date(payload.samplecollectionDate) : today}
          onChange={(value: Date | null) => {
            if (value) {
              setValue('samplecollectionDate', value.toISOString())
            } else {
              setValue('samplecollectionDate', new Date(today).toISOString())
            }
          }}
          minDate={startOfMonth(subMonths(today, 1))}
          maxDate={today}
          dateFormat="dd.MM.y"
          isClearable
          placeholderText={t('common:labels.calendar.start', 'Start Date')}
          locale={state?.me?.user.language || undefined}
          customInput={<CustomDateInput className={isSampleCollectionDateDefault ? 'default-date' : 'changed-date'} />}
        />
      </div>
      <InputWithTags
        savedSuggestions={payload.suggestions}
        defaultSuggestions={task.defaultSuggestions || []}
        label={t('tasks:labels.measurementTarget', 'Measurement target')}
        placeholder={t('tasks:placeholder.measurementTargetHygiene', 'Measurement target')}
        onChange={setValue}
        maxCharacters={100}
      />
      <AromiDishSelector task={task} payload={payload} setValue={setValue} />
      <PerformTaskForm
        onAssetSaved={onAssetSaved}
        onAssetRemoved={onAssetRemoved}
        onInputChange={setValue}
        payload={payload}
        handleRemove={
          !!task.scheduleInstance || task.status === EventStatusCode.IN_PROGRESS ? handleRemoveTask : undefined
        }
        setValue={setValue}
        confirm={
          task.scheduleInstance || task.status === EventStatusCode.IN_PROGRESS
            ? t(
                'tasks:labels.confirmRemoveTask',
                'Are you sure you want to delete this task? This will also remove any associated resolved alarms.'
              )
            : undefined
        }
        hasCurrentEvent={!!hasCurrentEvent}
        currentEventId={currentEventId}
      />
    </FormContainer>
  )
}

export default PerformHygieneTask
